import React from 'react';
import { Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { numberSeparator } from 'number-separator-js';
import { usePdpEvaluation } from '@/api/hooks/usePdpEvaluation';
import EvaluationItem from '@/components/item/EvaluationItem';
import MemberItem from '@/components/item/MemberItem';
import FundraisingHistoryItem from '@/components/item/FundraisingHistoryItem';
import ListItems from '@/components/ListItems';
import { FundraisingHistory, MemberDTO } from '@/types/models';
import timeFormat from '@/utils/timeFormat';
import LoadingState from '@/components/LoadingState';
import { IconSvg } from '@/components/Icon';
export default function PDPEvaluation() {
  const {
    data,
    isLoading
  } = usePdpEvaluation();
  const texts = {
    title: 'ارزیابی زیما',
    reportTitle: 'گزارش اعتبار‌سنجی',
    historyTitle: 'سوابق تأمین مالی',
    description: `گواهی اعتبارسنجی بانکی شرکت، اعضای هیأت مدیره و مدیرعامل از سامانه ${data?.verificationSystem?.title ?? 'آیس (اعتبار یکپارچه سپهر)'}`,
    dataTitle: 'تاریخ اعتبارسنجی:'
  };
  const items = data ? [{
    icon: <IconSvg component='EvaluationItem' width={40} height={40} viewBox='0 0 40 40' fill='#03904E' />,
    title: 'پیش‌بینی مجموع درآمد در سال',
    subTitle: `${numberSeparator({
      text: (+data?.evaluation?.annualIncomeEstimation).toString()
    })}`,
    color: 'approve.text',
    bgColor: 'approve.bg4',
    borderBgColor: 'approve.bg3'
  }, {
    icon: <IconSvg component='EvaluationSend' width={40} height={40} viewBox='0 0 40 40' fill='#007AFF' />,
    title: 'پیش‌بینی مجموع هزینه‌های عملیاتی در سال',
    subTitle: `${numberSeparator({
      text: (+data?.evaluation?.annualCostEstimation).toString()
    })}`,
    color: 'primaryBrand.text',
    bgColor: 'primaryBrand.bg5',
    borderBgColor: 'primaryBrand.bg4'
  }] : [];
  return isLoading ? <LoadingState data-sentry-element="LoadingState" data-sentry-component="PDPEvaluation" data-sentry-source-file="PDPEvaluation.tsx" /> : <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing_m_nudge)'
  }} data-sentry-element="Box" data-sentry-component="PDPEvaluation" data-sentry-source-file="PDPEvaluation.tsx">
      <Box sx={{
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      gap: 'var(--spacing_xs)'
    }} data-sentry-element="Box" data-sentry-source-file="PDPEvaluation.tsx">
        <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 'var(--spacing_xs)',
        py: 'var(--spacing_xxs)'
      }} data-sentry-element="Box" data-sentry-source-file="PDPEvaluation.tsx">
          <IconSvg component='Evaluation' width='32' height='32' data-sentry-element="IconSvg" data-sentry-source-file="PDPEvaluation.tsx" />
          <Typography variant='headlineStrong__Wallfund' color='text.text' component='h2' id='section-2' data-sentry-element="Typography" data-sentry-source-file="PDPEvaluation.tsx">
            {texts.title}
          </Typography>
        </Box>
        <Grid spacing='var(--spacing_m)' container data-sentry-element="Grid" data-sentry-source-file="PDPEvaluation.tsx">
          {items.map((item, idx) => {
          return <Grid size={{
            md: 6,
            xs: 12
          }} key={'item_evaluation_' + idx}>
                <EvaluationItem icon={item.icon} title={item.title} subTitle={item.subTitle} color={item.color} bgColor={item.bgColor} borderBgColor={item.borderBgColor} />
              </Grid>;
        })}
        </Grid>
      </Box>

      <Box sx={{
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      gap: 'var(--spacing_xs)'
    }} data-sentry-element="Box" data-sentry-source-file="PDPEvaluation.tsx">
        <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 'var(--spacing_xs)',
        py: 'var(--spacing_xxs)'
      }} data-sentry-element="Box" data-sentry-source-file="PDPEvaluation.tsx">
          <IconSvg component='Member' width='32' height='32' data-sentry-element="IconSvg" data-sentry-source-file="PDPEvaluation.tsx" />
          <Typography variant='headlineStrong__Wallfund' color='text.text' component='h2' data-sentry-element="Typography" data-sentry-source-file="PDPEvaluation.tsx">
            {texts.reportTitle}
          </Typography>
        </Box>
        <Typography sx={{
        typography: {
          xs: 'bodyRegular__Wallfund',
          md: 'bodyRegular__Wallfund'
        }
      }} color='text.text' data-sentry-element="Typography" data-sentry-source-file="PDPEvaluation.tsx">
          {texts.description}
        </Typography>
        <Typography variant='captionSmallMedium__Wallfund' sx={{
        width: 'fit-content',
        borderRadius: 'var(--corner_radius_circular)',
        px: 'var(--spacing_xxs)'
      }} data-sentry-element="Typography" data-sentry-source-file="PDPEvaluation.tsx">
          {texts.dataTitle}
          {data?.evaluation?.date && timeFormat(new Date(data.evaluation.date))}
        </Typography>

        <ListItems<MemberDTO> items={data?.evaluation?.members || []} id='evaluationMembers' customComponent={item => <MemberItem data={item} />} data-sentry-element="ListItems" data-sentry-source-file="PDPEvaluation.tsx" />
      </Box>

      <Box sx={{
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      gap: 'var(--spacing_xs)'
    }} data-sentry-element="Box" data-sentry-source-file="PDPEvaluation.tsx">
        {(data?.fundraisingHistory || []).length > 0 && <>
            <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 'var(--spacing_xs)',
          py: 'var(--spacing_xxs)'
        }}>
              <IconSvg component='History' width='32' height='32' />
              <Typography variant='headlineStrong__Wallfund' color='text.text' component='h2'>
                {texts.historyTitle}
              </Typography>
            </Box>

            <ListItems<FundraisingHistory> items={data?.fundraisingHistory || []} id='fundraisingHistory' customComponent={(item, idx) => <FundraisingHistoryItem data={item} index={idx || 0} />} />
          </>}
      </Box>
    </Box>;
}